/**
 * Chế độ đề xuất (theo dõi thay đổi) của `.chilldoc` — APM-CD2-h, docs/plan-chilldoc-v2.md §7.1.
 *
 * Chữ đề xuất **chèn** mang mark `suggestInsert`, chữ đề xuất **xoá** mang mark `suggestDelete`
 * (không xoá thật cho tới khi có người chấp nhận). Cả hai cùng bộ thuộc tính: id của đề xuất,
 * tác giả, giờ (ISO). Không có bảng riêng — đề xuất nằm trong tệp như bình luận.
 *
 * Hàm ở đây thuần trên ProseMirror JSON (không phụ thuộc TipTap). Ngoài trình soạn (người sửa được
 * hoặc người đề xuất), đề xuất **chưa duyệt** thì chưa có hiệu lực: xem trước, chế độ đọc, người
 * chỉ xem, bản sạch / bản chụp chia sẻ, slide và markdown đầu tệp (tìm kiếm, AI) dùng
 * `rejectedDoc` — bản **như thể đã từ chối** (người dùng chốt sau QA 2026-10-13; trước đó là "như
 * thể đã chấp nhận", tức chữ của người đề xuất lọt ra như chữ đã duyệt). Lưu `.docx` vẫn ghi
 * `w:ins` / `w:del` — "chưa duyệt" đúng nghĩa của Word.
 */
export const SUGGEST_INSERT_MARK = 'suggestInsert';
export const SUGGEST_DELETE_MARK = 'suggestDelete';
const hasMark = (node, type) => (node.marks ?? []).some((mark) => mark.type === type);
/** Tài liệu có đề xuất nào chưa xử lý không. */
export function hasSuggestions(doc) {
    if (hasMark(doc, SUGGEST_INSERT_MARK) || hasMark(doc, SUGGEST_DELETE_MARK))
        return true;
    return (doc.content ?? []).some(hasSuggestions);
}
/**
 * Bản **như thể đã chấp nhận mọi đề xuất**: node inline mang `suggestDelete` bỏ đi, mark
 * `suggestInsert` gỡ ra. Node không có đề xuất trả lại **chính nó** (không sao chép).
 */
export function acceptedDoc(doc) {
    return accept(doc);
}
function accept(node) {
    let changed = false;
    let content = node.content;
    if (content) {
        const next = [];
        for (const child of content) {
            if (hasMark(child, SUGGEST_DELETE_MARK)) {
                changed = true;
                continue;
            }
            const accepted = accept(child);
            if (accepted !== child)
                changed = true;
            next.push(accepted);
        }
        if (changed)
            content = next;
    }
    let marks = node.marks;
    if (marks && hasMark(node, SUGGEST_INSERT_MARK)) {
        marks = marks.filter((mark) => mark.type !== SUGGEST_INSERT_MARK);
        changed = true;
    }
    if (!changed)
        return node;
    const { marks: _marks, content: _content, ...rest } = node;
    return {
        ...rest,
        ...(marks && marks.length ? { marks } : {}),
        ...(content ? { content } : {}),
    };
}
/**
 * Bản **như thể đã từ chối mọi đề xuất** — bản chưa duyệt mà người ngoài trình soạn thấy: node
 * inline mang `suggestInsert` bỏ đi (kể cả khi nó cũng mang `suggestDelete` — chữ chưa từng được
 * nhận), mark `suggestDelete` gỡ ra. Node không có đề xuất trả lại **chính nó** (không sao chép).
 */
export function rejectedDoc(doc) {
    return reject(doc);
}
function reject(node) {
    let changed = false;
    let content = node.content;
    if (content) {
        const next = [];
        for (const child of content) {
            if (hasMark(child, SUGGEST_INSERT_MARK)) {
                changed = true;
                continue;
            }
            const rejected = reject(child);
            if (rejected !== child)
                changed = true;
            next.push(rejected);
        }
        if (changed)
            content = next;
    }
    let marks = node.marks;
    if (marks && hasMark(node, SUGGEST_DELETE_MARK)) {
        marks = marks.filter((mark) => mark.type !== SUGGEST_DELETE_MARK);
        changed = true;
    }
    if (!changed)
        return node;
    const { marks: _marks, content: _content, ...rest } = node;
    return {
        ...rest,
        ...(marks && marks.length ? { marks } : {}),
        ...(content ? { content } : {}),
    };
}
/**
 * Số đề xuất chờ duyệt — cùng cách đếm với ngăn Xem lại (`suggestionsOf`): mỗi cặp loại + `id`
 * là một đề xuất, dù mark khác chia nó thành nhiều node.
 */
export function pendingSuggestionCount(doc) {
    const seen = new Set();
    const walk = (node) => {
        for (const mark of node.marks ?? []) {
            if (mark.type === SUGGEST_INSERT_MARK || mark.type === SUGGEST_DELETE_MARK) {
                seen.add(`${mark.type}:${String(mark.attrs?.['id'] ?? '')}`);
            }
        }
        for (const child of node.content ?? [])
            walk(child);
    };
    walk(doc);
    return seen.size;
}
//# sourceMappingURL=chilldoc-suggest.js.map